import ArrowForward from "@mui/icons-material/ArrowForward";
import { Box, Button, Link, Popover, Stack, Typography } from "@mui/material";
import { useEffect, useId, useState } from "react";
import { Link as RouterLink } from "react-router";
import { usePendo } from "../../analytics/hooks/use-pendo-watch";
import { ChannelIcon } from "../../channels/components/channel-icon";
import { programPath } from "../../program-tree/program-tree";
import { useTimingOverlap } from "../hooks/use-program-timeline";
import { sendTimeText } from "../program-timeline";
import { CHANNEL_LABELS, SegmentName, isChannel } from "./timeline-parts";

import type { ProgramTree } from "../../program-tree/program-tree";
import type { ListTiming, TimelineGroup, TimelineRow } from "../program-timeline";
import type { FC } from "react";

/** The people that a Communication of another program shares with the program's ones. */
interface Overlap {
  people: number;
  /** The titles of the program's Communications that reach them. */
  titles: string[];
}

const peopleText = (n: number): string => (n === 1 ? "1 person" : `${n} people`);

/**
 * A Communication of another program: its send time, title, program, segment and channel, and
 * "May overlap" when it reaches people that the program's Communications at the timing reach.
 */
const ElsewhereItem: FC<{ row: TimelineRow; tree: ProgramTree; overlap: Overlap | null }> = ({
  row,
  tree,
  overlap,
}) => {
  const path = row.program_id ? programPath(tree, row.program_id) : [];
  const channel = isChannel(row.channel) ? CHANNEL_LABELS[row.channel] : null;

  return (
    <Stack
      component="li"
      direction="row"
      useFlexGap
      sx={{ alignItems: "center", flexWrap: "wrap", gap: 1.5, fontSize: 12 }}
    >
      {row.send_at ? (
        <Box
          component="span"
          sx={{
            px: 1,
            borderRadius: "4px",
            bgcolor: "action.hover",
            fontFamily: "monospace",
            fontWeight: 600,
            lineHeight: "24px",
          }}
        >
          {sendTimeText(row.send_at)}
        </Box>
      ) : null}
      <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
        {row.title}
      </Typography>
      {path.length ? (
        <Stack component="span" direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
          <Box
            component="span"
            aria-hidden
            sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: path[0]?.theme_colour }}
          />
          <span>{path.map(({ title }) => title).join(" › ")}</span>
        </Stack>
      ) : null}
      {row.segment_name ? <SegmentName name={row.segment_name} fontSize={16} /> : null}
      {overlap ? (
        <Box
          component="span"
          title={`May reach the same people as: ${overlap.titles.join("; ")}`}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            height: 18,
            px: "6px",
            borderRadius: "9px",
            bgcolor: "#FEF3C7",
            color: "#92400E",
            fontSize: 11,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          {`May overlap · ${peopleText(overlap.people)}`}
        </Box>
      ) : null}
      {channel ? (
        <Box
          component="span"
          role="img"
          aria-label={channel}
          title={channel}
          sx={{ lineHeight: 0 }}
        >
          <ChannelIcon
            channel={isChannel(row.channel) ? row.channel : null}
            sx={{ fontSize: 18 }}
          />
        </Box>
      ) : null}
    </Stack>
  );
};

/**
 * The Communications of other programs at a timing, under "Also <when>". They cannot be edited
 * here, but they may reach the same people, and those whose segments share people with the
 * program's ones today show "May overlap". A timing of a person's date links to the event's
 * timeline, which shows each Communication that sends from it.
 */
export const ElsewhereDetails: FC<{
  timing: ListTiming;
  /** The person's date, such as "start_date", or null for a specific date. */
  eventKey: string | null;
  /** The event's name, such as "Start date". */
  eventName: string;
  tree: ProgramTree;
  layout: "list" | "timeline";
}> = ({ timing, eventKey, eventName, tree, layout }) => {
  const pendo = usePendo();
  const overlap = useTimingOverlap(
    timing.stop.rows.map(({ id }) => id),
    timing.elsewhere.map(({ id }) => id),
  );
  const overlaps = new Map(
    (overlap.data ?? []).map(({ id, people, overlaps: here }) => [
      id,
      {
        people,
        titles: timing.stop.rows
          .filter((row) => here.includes(row.id))
          .map(({ title }) => title ?? "")
          .toSorted((a, b) => a.localeCompare(b)),
      },
    ]),
  );
  const shown = overlaps.size;
  useEffect(() => {
    if (shown) pendo?.track("program-elsewhere-overlap-shown", { eventKey, count: shown, layout });
  }, [shown, pendo, eventKey, layout]);

  return (
    <Stack component="section" aria-label={`Also ${timing.when}`} spacing={1.25}>
      <Typography variant="body2" sx={{ fontSize: 13, color: "text.secondary" }}>
        <Box component="strong" sx={{ color: "text.primary" }}>
          Also {timing.when}
        </Box>
        {" · "}these belong to other programs and aren’t editable from here, but the same person may
        receive them.
        {shown ? " “May overlap” counts the people in both segments today." : ""}
      </Typography>
      {overlap.error ? (
        <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
          The overlap of the segments could not be checked.
        </Typography>
      ) : null}
      <Stack component="ul" spacing={1.25} sx={{ m: 0, p: 0, listStyle: "none" }}>
        {timing.elsewhere.map((row) => (
          <ElsewhereItem
            key={row.id}
            row={row}
            tree={tree}
            overlap={overlaps.get(row.id) ?? null}
          />
        ))}
      </Stack>
      {eventKey ? (
        <Link
          component={RouterLink}
          to={`/timeline/${eventKey}`}
          onClick={() => pendo?.track("program-elsewhere-see-every-clicked", { eventKey })}
          underline="hover"
          sx={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 0.75 }}
          data-analytics-id="program-elsewhere-see-every"
        >
          See every {eventName} communication
          <ArrowForward aria-hidden sx={{ fontSize: 18 }} />
        </Link>
      ) : null}
    </Stack>
  );
};

export const elsewhereText = (n: number): string =>
  n === 1
    ? "1 other communication sends to this timing"
    : `${n} other communications send to this timing`;

/** A timing's "N other communications send to this timing", which opens them in a popover. */
export const ElsewhereLink: FC<{ group: TimelineGroup; timing: ListTiming; tree: ProgramTree }> = ({
  group,
  timing,
  tree,
}) => {
  const pendo = usePendo();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const id = useId();

  return (
    <>
      <Button
        size="small"
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        aria-controls={anchor ? id : undefined}
        onClick={(event) => {
          pendo?.track("program-elsewhere-clicked", {
            eventKey: group.code,
            count: timing.elsewhere.length,
            layout: "timeline",
          });
          setAnchor(event.currentTarget);
        }}
        sx={{ ml: "auto", minWidth: 0, px: 0.5, fontSize: 12, fontWeight: 400 }}
        data-analytics-id="program-elsewhere-open"
      >
        {elsewhereText(timing.elsewhere.length)}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            id,
            role: "dialog",
            "aria-label": `Also ${timing.when}`,
            sx: { width: 420, maxWidth: "calc(100vw - 32px)", p: 2 },
          },
        }}
      >
        <ElsewhereDetails
          timing={timing}
          eventKey={group.code}
          eventName={group.name}
          tree={tree}
          layout="timeline"
        />
      </Popover>
    </>
  );
};
